import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, ArrowUpFromLine, CheckCircle2, Download, Send } from 'lucide-react';
import { useRequirePin } from './PinGuard';
import { obtenerAutorizacionHuella } from '../lib/stepup';
import { errorPasskey } from '../lib/webauthn';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal de transferencia (payout) desde una cuenta 1TouchPay.
 *
 * Extraido de Bancos.jsx el 2026-08-03 al agregar el modo "Enviar a 309": el archivo
 * estaba en 4704 lineas y el candado anti-monolito de vps_push.sh no permite que crezca.
 *
 * Dos modos:
 *  - Normal (3 pasos): Destino → Monto → Confirmar. Pide PIN.
 *  - Preset (2 pasos): el destino viene resuelto de entrada. Se usa para el traspaso
 *    habitual 308 → 309, que antes obligaba a tipear el CVU cada vez.
 *
 * @param {object}  otpAcc            cuenta origen { cvuId, balance, alias }
 * @param {object=} otpAcc.preset     { cuenta, alias, titular, referencia, skipPin, label }
 */
export default function OneTouchPayCashoutModal({ otpAcc, onClose, onSuccess }) {
  const token = localStorage.getItem('token');
  const preset = otpAcc?.preset || null;
  const [step, setStep] = useState(preset ? 0 : 1);  // 0=resolviendo preset, 1=Destino, 2=Monto, 3=Confirmar, 4=Resultado
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchValue, setSearchValue] = useState(preset?.cuenta || '');
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [referencia, setReferencia] = useState(preset?.referencia || '');
  const [result, setResult] = useState(null);
  const [clienteId, setClienteId] = useState('');
  const [duplicado, setDuplicado] = useState(null);
  const [clientes, setClientes] = useState([]);
  const balance = otpAcc?.balance || 0;
  const { requirePin, PinGuardUI } = useRequirePin();

  // Lista de clientes para atribuir el pago (opcional pero recomendado): sin esto
  // la salida queda en el ledger sin saber a quién correspondía.
  useEffect(() => {
    if (preset) return;  // traspaso interno entre cuentas propias: no aplica
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/bancos/options/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setClientes(await res.json());
      } catch (e) { console.error('clientes fetch error', e); }
    })();
  }, [preset, token]);

  const resolveAccount = useCallback(async (cuenta) => {
    const res = await fetch(`${API_URL}/api/bancos/1touchpay/resolve-account?cuenta=${encodeURIComponent(cuenta)}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const data = await res.json();
    if (!data.found) throw new Error(data.reason || 'Cuenta no encontrada');
    return data;
  }, [token]);

  // Modo preset: resolvemos el destino al abrir y arrancamos directo en el monto.
  useEffect(() => {
    if (!preset?.cuenta) return;
    let vivo = true;
    (async () => {
      try {
        const data = await resolveAccount(preset.cuenta);
        if (!vivo) return;
        setRecipient(data);
        setStep(2);
      } catch (e) {
        if (!vivo) return;
        // Si la API no resuelve la cuenta, usamos los datos que ya conocemos del CVU
        // destino para no bloquear el traspaso.
        setRecipient({ titular: preset.titular || preset.alias, cuenta: preset.cuenta, cvu: preset.cuenta });
        setStep(2);
      }
    })();
    return () => { vivo = false; };
  }, [preset, resolveAccount]);

  const handleLookup = async () => {
    if (!searchValue.trim()) return;
    setLoading(true); setError('');
    try {
      setRecipient(await resolveAccount(searchValue.trim()));
      setStep(2);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleAmount = () => {
    const monto = parseFloat(amount);
    if (!monto || monto <= 0) { setError('Ingresa un monto valido'); return; }
    if (monto > balance) { setError(`Saldo insuficiente (disponible: $${balance.toLocaleString('es-AR', { minimumFractionDigits: 2 })})`); return; }
    setError(''); setStep(3);
  };

  const cuerpoPayout = () => ({
    cuenta: recipient.cvu || recipient.cbu || recipient.cuenta || searchValue.trim(),
    monto: parseFloat(amount),
    referencia: referencia,
    cuit: recipient.cuit || '',
    cvu_id: otpAcc?.cvuId,
    cliente_id: clienteId || null,
  });

  const postPayout = async (body) => {
    const res = await fetch(`${API_URL}/api/bancos/1touchpay/payout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = {};
    try { data = await res.json(); } catch (e) { data = {}; }
    return { res, data };
  };

  // Un solo camino para mandar la plata. `extras` sirve para reintentar la MISMA
  // operación con la confirmación de duplicado adentro (confirmar_duplicado).
  // Ojo: este modal usa fetch(), no axios, así que el interceptor de step-up de
  // App.js no lo cubre y la huella se pide acá a mano.
  const enviarPayout = async (extras = {}) => {
    setLoading(true); setError(''); setDuplicado(null);
    try {
      const body = { ...cuerpoPayout(), ...extras };
      let { res, data } = await postPayout(body);
      const codigo = data?.detail?.code;

      if (res.status === 428 && codigo === 'sin_passkey') {
        setError('Este dispositivo no tiene huella registrada. Registralo en Seguridad.');
        setLoading(false); return;
      }
      if (res.status === 428 && codigo === 'step_up_required') {
        let autorizacion;
        try {
          autorizacion = await obtenerAutorizacionHuella(`${API_URL}/api`);
        } catch (e) {
          setError(e?.response
            ? (e.response.data?.detail?.message || e.response.data?.detail || 'No se pudo verificar la huella')
            : errorPasskey(e));
          setLoading(false); return;
        }
        ({ res, data } = await postPayout({ ...body, step_up_token: autorizacion }));
      }

      if (data.success) { setResult(data); setStep(4); }
      else if (data.duplicado_confirmable) { setDuplicado(data); setStep(5); }
      else {
        setError(data.error || data?.detail?.message || 'Error en la transferencia');
        if (step === 5) setStep(3);  // el error se lee en la pantalla de confirmación
      }
    } catch (e) { setError('Error de conexion'); if (step === 5) setStep(3); }
    setLoading(false);
  };

  const handleSend = async () => {
    // El traspaso interno 308 → 309 no pide PIN (decision del usuario): la plata no
    // sale del circuito, solo se mueve entre CVUs propios.
    if (!preset?.skipPin && !(await requirePin())) return;
    await enviarPayout();
  };

  const pasos = preset ? [2, 3] : [1, 2, 3];
  const nombrePaso = (s) => (s === 1 ? 'Destino' : s === 2 ? 'Monto' : 'Confirmar');

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="otp-cashout-modal">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 rounded-t-2xl bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-teal-100 dark:bg-teal-900/50">
                {preset ? <Send className="w-5 h-5 text-teal-600" /> : <ArrowUpFromLine className="w-5 h-5 text-teal-600" />}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-100" data-testid="otp-cashout-title">
                  {preset?.label || 'Transferir 1TouchPay'}
                </h3>
                <p className="text-xs text-gray-500">Saldo: ${balance.toLocaleString('es-AR', { minimumFractionDigits: 2 })}{otpAcc?.alias ? ` · ${otpAcc.alias}` : ''}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600" data-testid="otp-cashout-close">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {step >= 1 && step <= 3 && (
            <div className="flex items-center gap-2 mt-3">
              {pasos.map((s, i) => (
                <div key={s} className="flex items-center gap-1 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
                  <span className={`text-[10px] font-medium ${step >= s ? 'text-teal-700 dark:text-teal-300' : 'text-gray-400'}`}>{nombrePaso(s)}</span>
                  {i < pasos.length - 1 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-teal-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="mx-6 mt-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <p className="text-xs text-red-600 dark:text-red-400" data-testid="otp-cashout-error">{error}</p>
          </div>
        )}

        {step === 0 && (
          <div className="px-6 py-8 text-center" data-testid="otp-cashout-resolving">
            <p className="text-sm text-gray-500">Verificando cuenta destino...</p>
          </div>
        )}

        {step === 1 && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alias o CBU/CVU del destinatario</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)}
                  placeholder="ej: mi.alias.cuenta o 0000003100..."
                  onKeyDown={e => e.key === 'Enter' && handleLookup()}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  data-testid="otp-cashout-search" autoFocus />
              </div>
            </div>
            <button onClick={handleLookup} disabled={loading || !searchValue.trim()}
              className="w-full py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors" data-testid="otp-cashout-lookup-btn">
              {loading ? 'Verificando...' : 'Verificar destinatario'}
            </button>
          </div>
        )}

        {step === 2 && recipient && (
          <div className="px-6 py-5 space-y-4">
            <div className="p-3 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 text-xs space-y-0.5" data-testid="otp-cashout-recipient">
              <p className="font-bold text-gray-800 dark:text-gray-100 text-sm">{recipient.titular || recipient.nombre || 'Titular'}</p>
              {preset?.alias && <p className="text-teal-700 dark:text-teal-300 font-medium">Alias: {preset.alias}</p>}
              {recipient.cuit && <p className="text-gray-500">CUIT: {recipient.cuit}</p>}
              {(recipient.cvu || recipient.cbu || recipient.cuenta) && <p className="text-gray-500">Cuenta: {recipient.cvu || recipient.cbu || recipient.cuenta}</p>}
              {recipient.banco && <p className="text-gray-500">Banco: {recipient.banco}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto a transferir ($)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} min="1"
                onKeyDown={e => e.key === 'Enter' && handleAmount()}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" data-testid="otp-cashout-amount" autoFocus />
            </div>
            {!preset && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cliente <span className="text-xs font-normal text-gray-500">(opcional, recomendado)</span>
                </label>
                <select value={clienteId} onChange={e => setClienteId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  data-testid="otp-cashout-cliente">
                  <option value="">— Sin asignar —</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {!clienteId && (
                  <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400" data-testid="otp-cashout-cliente-warn">
                    Si no elegís cliente, esta salida va a quedar sin atribuir en el ledger.
                  </p>
                )}
              </div>
            )}
            {!preset && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referencia (opcional)</label>
                <input type="text" value={referencia} onChange={e => setReferencia(e.target.value)} maxLength={255}
                  placeholder="ej: Pago proveedor" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm" data-testid="otp-cashout-ref" />
              </div>
            )}
            <div className="flex gap-2">
              {!preset && <button onClick={() => { setStep(1); setError(''); }} className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Atrás</button>}
              <button onClick={handleAmount} className="flex-1 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition-colors" data-testid="otp-cashout-continue-btn">Continuar</button>
            </div>
          </div>
        )}

        {step === 3 && recipient && (
          <div className="px-6 py-5 space-y-4">
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 space-y-1.5" data-testid="otp-cashout-confirm-box">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase">Revisá antes de confirmar</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">${parseFloat(amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
              <p className="text-sm text-gray-700 dark:text-gray-300">→ {recipient.titular || recipient.nombre}{preset?.alias ? ` · ${preset.alias}` : ''}</p>
              <p className="text-xs text-gray-500">{recipient.cvu || recipient.cbu || recipient.cuenta || searchValue}</p>
              {clienteId && <p className="text-xs text-blue-700 dark:text-blue-300 font-medium" data-testid="otp-cashout-confirm-cliente">Cliente: {clientes.find(c => c.id === clienteId)?.name || ''}</p>}
              {referencia && <p className="text-xs text-gray-500">Ref: {referencia}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setStep(2); setError(''); }} className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Atrás</button>
              <button onClick={handleSend} disabled={loading}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors" data-testid="otp-cashout-send-btn">
                {loading ? 'Transfiriendo...' : 'Confirmar transferencia'}
              </button>
            </div>
          </div>
        )}

        {/* Segundo pago igual al mismo destino cuando el anterior SALIO BIEN: no se
            bloquea, se pregunta. El caso ambiguo (timeout / 5xx) sigue bloqueado duro
            desde el backend y no llega nunca acá. */}
        {step === 5 && duplicado && (
          <div className="px-6 py-5 space-y-4" data-testid="otp-cashout-duplicado">
            <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 space-y-1.5">
              <p className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase">Pago repetido</p>
              <p className="text-sm text-gray-800 dark:text-gray-200" data-testid="otp-cashout-duplicado-msg">{duplicado.error}</p>
              {duplicado.previous_external_id && (
                <p className="text-[10px] text-gray-500">Pago anterior: {duplicado.previous_external_id}</p>
              )}
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Si es el mismo pago que ya salió, cancelá. Si es un segundo pago distinto, confirmá y sale igual.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setDuplicado(null); setStep(3); }}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                data-testid="otp-cashout-duplicado-cancel-btn">No, cancelar</button>
              <button onClick={() => enviarPayout({ confirmar_duplicado: true })} disabled={loading}
                className="flex-1 py-2.5 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-50 transition-colors"
                data-testid="otp-cashout-duplicado-confirm-btn">
                {loading ? 'Transfiriendo...' : 'Sí, es otro pago'}
              </button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="px-6 py-6 text-center space-y-3" data-testid="otp-cashout-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-bold text-gray-900 dark:text-gray-100">Transferencia enviada</p>
            <p className="text-sm text-gray-500">${parseFloat(amount).toLocaleString('es-AR', { minimumFractionDigits: 2 })} → {recipient?.titular || ''}</p>
            <p className="text-[10px] text-gray-400">ID: {result.external_id} · Estado: {result.payout?.status || 'OK'}</p>
            {result.payout?.transaction_id && (
              <button onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/api/bancos/1touchpay/comprobante/${result.payout.transaction_id}.png`, { headers: { 'Authorization': `Bearer ${token}` } });
                  if (!res.ok) return;
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `comprobante_1touchpay_${result.payout.transaction_id}.png`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch (e) { console.error('Comprobante:', e); }
              }} className="w-full py-2.5 rounded-lg border-2 border-teal-600 text-teal-700 dark:text-teal-300 text-sm font-semibold hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors flex items-center justify-center gap-2" data-testid="otp-cashout-receipt-btn">
                <Download className="w-4 h-4" /> Descargar comprobante (PNG)
              </button>
            )}
            <button onClick={onSuccess} className="w-full py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700" data-testid="otp-cashout-done-btn">Listo</button>
          </div>
        )}
      </div>
      {PinGuardUI}
    </div>
  );
}
